import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, isDemoMode } from "@/lib/supabase";
import {
  fallbackRefetchMs,
  isProcessing,
  rpcErrorCode,
  type ChannelSessionRow,
  type ConnectionChannel,
  type ConnectionMethod,
  type ConnectionSession,
} from "@/lib/connections";

// Ponte painel <-> bot: o painel só chama RPCs e lê connection_sessions (RLS).
// O status ao vivo chega por Supabase Realtime, com polling curto de reserva
// enquanto o bot está processando.

export const connectionKeys = {
  session: (channel: ConnectionChannel) => ["connection-session", channel] as const,
  mtproto: ["mtproto-session"] as const,
  whatsapp: ["whatsapp-session"] as const,
  telegramAccounts: ["telegram-accounts"] as const,
  realtime: ["connections-realtime"] as const,
};

/** Status do canal Realtime (SUBSCRIBED, CHANNEL_ERROR, TIMED_OUT, CLOSED). */
export function useRealtimeFallback(): () => number | false {
  const queryClient = useQueryClient();
  return () => fallbackRefetchMs(queryClient.getQueryData<string>(connectionKeys.realtime));
}

const PROCESSING_REFETCH_MS = 2_000;

/** Erro de RPC com código estável (ex.: invalid_phone) para a UI traduzir. */
export class ConnectionRpcError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "ConnectionRpcError";
  }
}

async function callRpc(fn: string, args: Record<string, unknown>): Promise<ConnectionSession> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    throw new ConnectionRpcError(rpcErrorCode(error.message));
  }
  return data as ConnectionSession;
}

async function fetchConnectionSession(channel: ConnectionChannel): Promise<ConnectionSession | null> {
  const { data, error } = await supabase
    .from("connection_sessions")
    .select(
      "id, tenant_id, channel, method, status, pending_action, phone_masked, payload, error, retry_after_seconds, expires_at, created_at, updated_at",
    )
    .eq("channel", channel)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ConnectionSession | null;
}

export function useConnectionSession(channel: ConnectionChannel) {
  const fallback = useRealtimeFallback();
  return useQuery<ConnectionSession | null, Error>({
    queryKey: connectionKeys.session(channel),
    queryFn: () => fetchConnectionSession(channel),
    staleTime: 5_000,
    refetchInterval: (query) => (isProcessing(query.state.data) ? PROCESSING_REFETCH_MS : fallback()),
  });
}

export interface MtprotoSessionRow extends ChannelSessionRow {
  phone: string | null;
  last_connected_at: string | null;
}

async function fetchMtprotoSession(): Promise<MtprotoSessionRow | null> {
  const { data, error } = await supabase
    .from("mtproto_sessions")
    .select("status, revoked_reason, last_health_check_at, phone, last_connected_at")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as MtprotoSessionRow | null;
}

export function useMtprotoSession() {
  const fallback = useRealtimeFallback();
  return useQuery<MtprotoSessionRow | null, Error>({
    queryKey: connectionKeys.mtproto,
    queryFn: fetchMtprotoSession,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: () => fallback(),
  });
}

/** Mutations da conexão de um canal. Toda resposta já atualiza o cache. */
export function useConnectionActions(channel: ConnectionChannel) {
  const queryClient = useQueryClient();
  const key = connectionKeys.session(channel);
  const onSuccess = (session: ConnectionSession): void => {
    queryClient.setQueryData(key, session);
  };
  const invalidateChannel = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: channel === "whatsapp" ? connectionKeys.whatsapp : connectionKeys.mtproto });
  };

  return {
    start: useMutation<ConnectionSession, Error, { phone: string; method?: ConnectionMethod; riskAccepted?: boolean }>({
      mutationFn: ({ phone, method, riskAccepted }) =>
        callRpc("start_connection_session", {
          p_channel: channel,
          p_phone: phone,
          p_method: method ?? null,
          p_risk_accepted: riskAccepted ?? false,
        }),
      onSuccess,
    }),
    submitCode: useMutation<ConnectionSession, Error, { sessionId: string; code: string }>({
      mutationFn: ({ sessionId, code }) => callRpc("submit_connection_code", { p_session_id: sessionId, p_code: code }),
      onSuccess,
    }),
    submitPassword: useMutation<ConnectionSession, Error, { sessionId: string; password: string }>({
      mutationFn: ({ sessionId, password }) =>
        callRpc("submit_connection_password", { p_session_id: sessionId, p_password: password }),
      onSuccess,
    }),
    cancel: useMutation<ConnectionSession, Error, { sessionId: string }>({
      mutationFn: ({ sessionId }) => callRpc("cancel_connection_session", { p_session_id: sessionId }),
      onSuccess,
    }),
    disconnect: useMutation<ConnectionSession, Error, void>({
      mutationFn: () => callRpc("disconnect_connection", { p_channel: channel }),
      onSuccess: async (session) => {
        onSuccess(session);
        await invalidateChannel();
      },
    }),
  };
}

const REALTIME_TABLES: ReadonlyArray<{ table: string; keys: ReadonlyArray<readonly unknown[]> }> = [
  { table: "connection_sessions", keys: [connectionKeys.session("whatsapp"), connectionKeys.session("telegram")] },
  { table: "whatsapp_sessions", keys: [connectionKeys.whatsapp] },
  { table: "mtproto_sessions", keys: [connectionKeys.mtproto] },
  { table: "telegram_accounts", keys: [connectionKeys.telegramAccounts] },
];

/** Assina mudanças das tabelas de conexão (RLS filtra pelo tenant) e
 *  invalida os caches correspondentes. Montar uma vez por tela. */
export function useConnectionsRealtime(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isDemoMode()) return;
    const channel = supabase.channel("connections-status");
    for (const { table, keys } of REALTIME_TABLES) {
      channel.on("postgres_changes", { event: "*", schema: "public", table }, () => {
        for (const key of keys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      });
    }
    // #9: sem SUBSCRIBED os hooks caem no refetch de reserva (12s).
    channel.subscribe((status: string) => {
      queryClient.setQueryData(connectionKeys.realtime, status);
    });
    return () => {
      queryClient.setQueryData(connectionKeys.realtime, "CLOSED");
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
